import CRUDService from 'common/services/crud'
import { stripEmptyKeys } from 'common/utils'
import querystring from 'querystring'

class VenueParticipationCRUDService extends CRUDService {
    constructor() {
        super('venueParticipation')
    }

    ridersWithBalance = (venueId, params = {}) => {
        if (!venueId)
            return Promise.reject('Missing Venue ID.')

        return this.$http.get(this.url + 'ridersWithBalance/', {
            params: {
                ...params,
                venue: venueId
            }
        })
    }

    ridersForOrganizer = (venueId, params = {}) => {
        if (!venueId)
            return Promise.reject('Missing Venue ID.')

        return this.$http.get(this.url + 'forOrganizer/', {
            params: {
                ...params,
                venue: venueId
            }
        })
    }

    checkIn = (participationId, { commit = false, authorize = false, forceRiderStripe = false } = {}) => {
        if (!participationId)
            return Promise.reject('Missing Participation ID.')

        return this.$http.post(this.getObjectURL(participationId) + 'checkIn/', {
            commit,
            authorize,
            forceRiderStripe
        })
    }

    checkinRider = (participationId, { commit = false, authorize = false } = {}) => !participationId
        ? Promise.reject('Missing Participation ID.')
        : this.$http.put(this.getObjectURL(participationId) + 'checkinRider/', {
            commit,
            authorize,
            proceedOnPaymentFailure: true
        })

    feiCheckout = (participationId, horseId, action) =>
        this.$http.post(this.getObjectURL(participationId) + `feiCheckout/${horseId}/`, { action })

    horseCheckOutList = (participationId) => {
        if (!participationId)
            return Promise.reject('Missing Participation ID.')

        return this.$http.get(this.getObjectURL(participationId) + 'horseCheckOutList/')
    }

    horseCheckOut = (participationId, horseIds) => {
        if (!participationId)
            return Promise.reject('Missing Participation ID or list of horse ids.')


        return this.$http.post(this.getObjectURL(participationId) + 'horseCheckOut/', { horseIds })
    }

    checkOut = (participationId, commit = false, {
        checkoutWithoutPayment = false,
        forceRiderStripe = false,
        forceCashPayment = false,
        payment_intent_id = null
    } = {}) => {
        if (!participationId)
            return Promise.reject('Missing Participation ID.')

        return this.$http.post(this.getObjectURL(participationId) + 'checkOut/', {
            commit,
            checkoutWithoutPayment,
            forceRiderStripe,
            forceCashPayment,
            payment_intent_id
        })
    }

    undoCheckOut = (participationId) => {
        if (!participationId)
            return Promise.reject('Missing Participation ID.')

        return this.$http.post(this.getObjectURL(participationId) + 'undoCheckOut/')
    }

    onboardingEmails = (venueId, params = {}) => !venueId
        ? Promise.reject('Missing venue ID.')
        : this.$http.get(this.url + 'onboardingEmails/', { params: { venue: venueId, ...params } })

    sendOnboardingEmails = (venueId) => !venueId
        ? Promise.reject('Missing venue ID.')
        : this.$http.post(this.url + 'sendOnboardingEmails/', {}, { params: { venue: venueId } })

    sendOnboardingEmail = (participationId, params = {}) => !participationId
        ? Promise.reject('Missing venue participation ID.')
        : this.$http.post(this.getObjectURL(participationId) + 'sendOnboardingEmail/', params)

    getForRiderAndVenue = (riderId, venueId) => (!riderId || !venueId)
        ? Promise.reject('Missing rider or venue ID.')
        : this.$http.get(this.url + 'getForRiderAndVenue/', {
            params: {
                rider: riderId,
                venue: venueId
            }
        })

    ridersWithBalancePDF = (params = {}) => !params.venue
        ? Promise.reject('Missing venue ID')
        : window.open(this.url + 'ridersWithBalancePDF/?' + querystring.stringify(stripEmptyKeys(params)))

    ridersWithBalanceCSV = (params = {}) => !params.venue
        ? Promise.reject('Missing venue ID')
        : window.open(this.url + 'ridersWithBalanceCSV/?' + querystring.stringify(stripEmptyKeys(params)))

    ridersWithBalanceXLSX = (params = {}) => !params.venue
        ? Promise.reject('Missing venue ID')
        : window.open(this.url + 'ridersWithBalanceXLSX/?' + querystring.stringify(stripEmptyKeys(params)))

    riderAgesXLSX = (params = {}) => !params.venue
        ? Promise.reject('Missing venue ID')
        : window.open(this.url + 'ridersAgesXLSX/?' + querystring.stringify(stripEmptyKeys(params)))

    allParticipantsPDF = (params = {}) => !params.venue
        ? Promise.reject('Missing venue ID')
        : window.open(this.url + 'allParticipantsPDF/?' + querystring.stringify(stripEmptyKeys(params)))

    /* email templates */

    ridersForSendEmails = (venueId, params = {}) => {
        if (!venueId)
            return Promise.reject('Missing Venue ID.')

        const p = { ...params, venue: venueId }

        // UI-only flag must not be sent
        delete p.vec_filter_enabled

        // Only send when non-empty
        if (!Array.isArray(p.venue_event_categories) || p.venue_event_categories.length === 0)
            delete p.venue_event_categories

        const sp = new URLSearchParams()
        Object.keys(p).forEach((key) => {
            const val = p[key]
            if (val === undefined) return

            if (Array.isArray(val))
                val.forEach(v => sp.append(key, v))
            else
                sp.append(key, val)

        })

        return this.$http.get(this.url + 'forSendEmails/?' + sp.toString())
    }


    sendTemplateEmails = (venueId, templateId, params = {}) => {
        if (!venueId || !templateId)
            return Promise.reject('Missing Venue or Email template ID.')

        return this.$http.post(this.url + 'sendTemplateEmails/?' + querystring.stringify(stripEmptyKeys(params)), {
            template: templateId,
            venue: venueId
        })
    }
    sendTemplateEmail = (venueId, participationId, params = {}) => {
        if (!venueId || !participationId)
            return Promise.reject('Missing Venue or Participation ID.')

        return this.$http.post(this.getObjectURL(participationId) + 'sendTemplateEmail/', {
            ...params,
            venue: venueId
        })
    }


    uploadEntriesDE = (venueId, data) => !venueId
        ? Promise.reject('Missing Venue ID.')
        : !data
            ? Promise.reject('Missing data.')
            : this.$http.post(`${this.url}uploadEntriesDE/`, data)

    uploadTorisInfo = (venueId, data) => !venueId
        ? Promise.reject('Missing Venue ID.')
        : !data
            ? Promise.reject('Missing data.')
            : this.$http.post(`${this.url}uploadTorisInfo/`, data, { params: { venue: venueId } })

    uploadRossonEntries = (venueId, data) => !venueId
        ? Promise.reject('Missing Venue ID.')
        : !data
            ? Promise.reject('Missing data.')
            : this.$http.post(`${this.url}uploadEntriesCH/`, data, { params: { venue: venueId } })

    syncTorisQueue = (venueId, { dryRun = false } = {}) => !venueId
        ? Promise.reject('Missing Venue ID.')
        : this.$http.post(`${this.url}syncTorisQueue/`, {}, {
            params: {
                venue: venueId,
                ...(dryRun ? { dry_run: 1, preview_only: 1 } : {})
            }
        })

    replayArchivedTorisMessages = (venueId, { dryRun = false, forceFull = false } = {}) => !venueId
        ? Promise.reject('Missing Venue ID.')
        : this.$http.post(`${this.url}replayArchivedTorisMessages/`, {}, {
            params: {
                venue: venueId,
                ...(dryRun ? { dry_run: 1 } : {}),
                ...(forceFull ? { force_full: 1 } : {})
            }
        })

    exportImportLogXLSX = (rows) =>
        this.$http.post(`${this.url}exportImportLogXLSX/`, { rows }, { responseType: 'blob' })

    processRiderNoShow = (venueParticipationId) => !venueParticipationId
        ? Promise.reject('Missing venue participation ID')
        : this.$http.post(this.getObjectURL(venueParticipationId) + 'process_rider_no_show/')

    processRiderUndoNoShow = (venueParticipationId) => !venueParticipationId
        ? Promise.reject('Missing venue participation ID')
        : this.$http.post(this.getObjectURL(venueParticipationId) + 'process_rider_undo_no_show/')

    saveRiderPreselectedData = (venueParticipationId, { consumables, horsesPerCategory, VECParticipations, comment }) =>
        !venueParticipationId
            ? Promise.reject('saveRiderPreselectedData: Missing venue participation ID.')
            : this.$http.post(this.getObjectURL(venueParticipationId) + 'saveRiderPreselectedData/', {
                consumables,
                horsesPerCategory,
                VECParticipations,
                comment
            })

    generatePreshowSummaryPDFurl = (venueParticipationId) => {
        if (!venueParticipationId)
            return Promise.reject('Missing participation ID')

        return this.getObjectURL(venueParticipationId) + 'preshowSummaryPDF/'
    }

    generatePreshowSummaryPrivatePDFurl = (venueParticipationId) => {
        if (!venueParticipationId)
            return Promise.reject('Missing participation ID')

        return this.getObjectURL(venueParticipationId) + 'preshowPDF/'
    }

    sendGeneratePreshowSummaryPDF = (venueParticipationId) => {
        if (!venueParticipationId)
            return Promise.reject('Missing participation ID')
        return this.$http.post(this.getObjectURL(venueParticipationId) + 'sendPreshowSummaryPDF/')
    }

    preshowSummaryTotal = (venueParticipationId) => !venueParticipationId
        ? Promise.reject('Missing venue participation ID')
        : this.$http.get(this.getObjectURL(venueParticipationId) + 'preshowSummaryTotal/')

    preshowSummaryData = (venueParticipationId) => !venueParticipationId
        ? Promise.reject('Missing venue participation ID')
        : this.$http.get(this.getObjectURL(venueParticipationId) + 'preshowSummaryData/')

    preshowPayment = (venueParticipationId, return_url) => !venueParticipationId
        ? Promise.reject('Missing venue participation ID')
        : this.$http.post(this.getObjectURL(venueParticipationId) + 'preshowPayment/', {
            return_url
        })

    preshowPaymentVerify = (venueParticipationId, session_id) => !venueParticipationId
        ? Promise.reject('Missing venue participation ID')
        : this.$http.post(this.getObjectURL(venueParticipationId) + 'preshowPaymentVerify/', {
            session_id
        })


    preselectedDataExport = (venueID) => !venueID
        ? Promise.reject('Missing venue ID')
        : window.open(this.url + 'exportPreselectedDataXLS/?' + querystring.stringify({ venueID }))

    notifySVPSusers = (venueID) => !venueID
        ? Promise.reject('Missing venue ID')
        : window.open(this.url + 'notifySVPSusers/?' + querystring.stringify({ venueID }))


    massCheckout = (categoryID) => !categoryID
        ? Promise.reject('Missing category ID.')
        : this.$http.post(`${this.url}massCheckout/`, { categoryID })

    uploadXlsxTransactions = (venueId, data) => !venueId
        ? Promise.reject('Missing Venue ID.')
        : !data
            ? Promise.reject('Missing data.')
            : this.$http.post(this.getObjectURL(venueId) + 'uploadXlsxTransactions/', data)


    uploadHorseCheckins = (venueId, data) => !venueId
        ? Promise.reject('Missing Venue ID.')
        : !data
            ? Promise.reject('Missing data.')
            : this.$http.post(this.getObjectURL(venueId) + 'uploadHorseCheckIns/', data)


    send3DSecureEmail = (participationId, params = {}) => !participationId
        ? Promise.reject('Missing venue participation ID.')
        : this.$http.post(this.getObjectURL(participationId) + 'send3DSecureEmail/', params)

    sepaXML = async (venueId) => {
        if (!venueId) return Promise.reject('Missing Venue ID.')
        const xml = await this.$http.get(this.getObjectURL(venueId) + 'generateSepaXML/')
        const blob = new Blob([xml], { type: 'application/xml' })
        const a = Object.assign(document.createElement('a'), {
            href: URL.createObjectURL(blob),
            download: `${venueId}-SEPA-Payments.xml`
        })
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
    }

    confirmSepaPayment = (venueId) => !venueId
        ? Promise.reject('Record has no ID.')
        : this.$http.post(this.getObjectURL(venueId) + 'confirmSepaPayment/')

    discardSepaPayment = (venueId) => !venueId
        ? Promise.reject('Record has no ID.')
        : this.$http.post(this.getObjectURL(venueId) + 'discardSepaPayment/')

}

export default new VenueParticipationCRUDService()

class VenueParticipationExternalCostCRUDService extends CRUDService {
    constructor() {
        super('venueParticipationExternalCost')
    }
}

export const VenueParticipationExternalCostService = new VenueParticipationExternalCostCRUDService()
